'use client';

import React, { useState, useEffect } from 'react';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface Quest {
  id: string;
  title: string;
  prompt: string;
  lens: string;
  namesActive: string[];
  ayahAnchor: string;
  ayahRef: string;
  duration: string;
  type: 'observation' | 'reflection' | 'action' | 'mizan' | 'restoration';
  difficulty: 'Hawas' | 'Tafakkur' | 'Hikmah';
  rolePromptVariants?: Record<string, string>;
  amanahDomain?: string;
}

interface VocationalLens {
  id: string;
  title: string;
  arabic: string;
  color: string;
  icon: string;
  description: string;
  amanahDomain: string;
  quests: Quest[];
}

// ─── QUEST DATA BY VOCATIONAL LENS ────────────────────────────────────────────

const VOCATIONAL_QUESTS: VocationalLens[] = [
  {
    id: 'engineer',
    title: 'Engineer',
    arabic: 'مُهَنْدِس',
    color: '#2d8a87',
    icon: '⚙',
    description: 'Systems analysis — decompose the infrastructure around you into its measured components',
    amanahDomain: 'Infrastructure as trust — every system built is an Amanah to the community it serves',
    quests: [
      {
        id: 'eng-1',
        title: 'Systems Decomposition Walk',
        prompt: 'Walk one block in your ward. Identify 3 infrastructure systems (water, energy, transit, communication). For each: What are its inputs? What are its outputs? Where is the Mizan — and where is it transgressed? Document with the Three Questions: Is it? What is it? When is/was it?',
        rolePromptVariants: {
          engineer: 'Map the load-bearing systems: what are the stress points, redundancies, and failure modes in this infrastructure? Where does the Taqdir of the system reveal its design limits?',
          teacher: 'What does this infrastructure teach? What curriculum is embedded in how this system was built — and what does its failure teach about Mizan?',
          farmer: 'How does this infrastructure interact with the soil and water cycles? Where does it disrupt the Sunnat Allah of the land?',
          'returning-citizen': 'Which communities does this infrastructure serve — and which does it bypass? Map the Mizan transgression in the distribution.',
          scientist: 'What measurable data does this system produce? What Taqdir is encoded in its parameters?',
          custodian: 'What does maintaining this system require? What is overlooked in its daily custodianship?',
        },
        lens: 'Taqdir as measurement — every infrastructure parameter is a decreed measure',
        namesActive: ['Al-Qadir', 'Al-Musawwir', 'Al-Khabir'],
        ayahAnchor: '"Every measurement given to a thing is prescribed by the owner of the highest embodiment of that concept."',
        ayahRef: 'Open Letter — Al-Qadir Section',
        duration: '45 min',
        type: 'observation',
        difficulty: 'Tafakkur',
        amanahDomain: 'Infrastructure as Amanah — Key 09 (Amanah) → Key 07 (Mizan)',
      },
      {
        id: 'eng-2',
        title: 'Sand and Glass Inventory',
        prompt: 'Identify 5 tools you use in your work. For each tool: trace it back to its raw materials. What Names of Allah are operative in the materials? What trust (Amanah) is embedded in the tool? "Allah is Al-Baseer; He provided us with vision, and entrusted to us sand and glass; the means to develop lenses." What lenses are you building?',
        rolePromptVariants: {
          engineer: 'Trace your engineering tools to their material origins: silicon, copper, steel. What Names compose these materials? What trust is embedded in the tool you build from them?',
          teacher: 'What teaching tools do you use? Trace them back: chalk is calcium, paper is cellulose, the screen is sand and glass. What Names are in your classroom?',
          farmer: 'Your tools are extensions of the earth: the hoe is iron from the ground, the seed is Al-Khaliq\'s design. What Names compose your farming tools?',
          'returning-citizen': 'What tools do you use to advocate and organize? What raw materials — human, material, spiritual — compose your work?',
          scientist: 'Identify 5 tools in your research. Trace them back to their origins. What Names of Allah are operative in the tools of your research?',
          custodian: 'Trace your custodial tools to their origins. What Names compose the tools of custodianship?',
        },
        lens: 'Mata as trust — tools are provisions whose purpose is what they reveal, not the tools themselves',
        namesActive: ['Al-Baseer', 'An-Nur', 'Al-Khaliq'],
        ayahAnchor: '"Allah is Al-Baseer; The All-Seeing, He provided us with vision, and entrusted to us sand and glass; the means to develop lenses, a microscope, or binoculars — tools."',
        ayahRef: 'Open Letter — Al-Qadir Section',
        duration: '30 min',
        type: 'reflection',
        difficulty: 'Hawas',
        amanahDomain: 'Tools as Mata — the lens is itself an Ayah',
      },
      {
        id: 'eng-3',
        title: 'Root Fission: ق-د-ر in Your Work',
        prompt: 'The root ق-د-ر has 12 meaning dimensions: measure, manifest, apportion, decree, ordain, decide, prepare, reason, power, accomplish, prevail, control. Identify which of these 12 dimensions are active in your current project. Which ones are you exercising? Which ones belong only to Al-Qadir? Where is the boundary between your agency and His decree?',
        rolePromptVariants: {
          engineer: 'In your engineering work: which of the 12 dimensions of ق-د-ر are you exercising? Where does your measurement end and His decree begin?',
          teacher: 'In your teaching: which dimensions of ق-د-ر are active in the knowledge you transmit? What does it mean to "prepare" (one of the 12) a student for recognition?',
          farmer: 'In your farming: which dimensions of ق-د-ر govern the soil, the seed, the season? Which ones are yours to exercise — and which belong only to Al-Qadir?',
          'returning-citizen': 'In your advocacy: which dimensions of ق-د-ر are active in your reform work? What does "prevail" mean when oriented toward Islah?',
          scientist: 'In your research: which dimensions of ق-د-ر are operative in your methodology? What does it mean to "reason" (one of the 12) through data?',
          custodian: 'In your custodianship: which dimensions of ق-د-ر are active in maintaining a space? What does "apportion" mean in the context of care?',
        },
        lens: 'Root Fission — second-order fission; crack open the Name operative in your work',
        namesActive: ['Al-Qadir', 'Al-Alim', 'Al-Hakim'],
        ayahAnchor: '"The pens have been lifted and the pages have dried." — Hadith 19, 40 Hadith an-Nawawi',
        ayahRef: 'Hadith 19',
        duration: '20 min',
        type: 'reflection',
        difficulty: 'Hikmah',
        amanahDomain: 'Decree recognition — Key 03 (Sunnat Allah) → Key 10 (Haqq)',
      },
    ],
  },
  {
    id: 'teacher',
    title: 'Teacher',
    arabic: 'مُعَلِّم',
    color: '#c9a84c',
    icon: '✎',
    description: 'Curriculum as Ayat — what Names are you transmitting through your teaching?',
    amanahDomain: 'Knowledge as trust — "He taught Adam the names — all of them." Teaching is the continuation of this trust.',
    quests: [
      {
        id: 'teach-1',
        title: 'Rain Hadith Soil Assessment',
        prompt: 'The Prophet ﷺ described three types of learners: soil that absorbs and grows, soil that holds and shares, and soil that neither absorbs nor produces. Assess your current students or community: which soil type is each person? What does Al-Alim know about each soil that you don\'t? How does your curriculum respond to each type?',
        rolePromptVariants: {
          engineer: 'Apply the rain hadith to your engineering team: which members absorb and build, which hold and share knowledge, which are disengaged? How does your systems design account for each type?',
          teacher: 'Map your classroom as soil: which students absorb and grow, which hold and share, which neither absorb nor produce? What does Al-Alim know about each soil that you don\'t?',
          farmer: 'The rain hadith is literally about soil. Apply it to your garden: which plots absorb and grow, which hold water, which drain without producing? What does this teach about cultivation?',
          'returning-citizen': 'Apply the rain hadith to your community: which members absorb and grow from resources, which hold and share, which are unreachable by current approaches? What new approach does each soil type require?',
          scientist: 'Apply the rain hadith to your research community: which colleagues absorb and build on knowledge, which hold and share data, which are isolated? How does your research design account for each?',
          custodian: 'Apply the rain hadith to the spaces you maintain: which areas absorb care and flourish, which hold their state, which resist improvement? What does each type require?',
        },
        lens: 'Ilm as provision — knowledge is rain; the teacher\'s work is to recognize the soil',
        namesActive: ['Al-Alim', 'Ar-Razzaq', 'Al-Latif'],
        ayahAnchor: '"The similitude of guidance and knowledge that Allah has sent me with is like heavy rain that fell on a land."',
        ayahRef: 'Narrated Abu Musa · Al-Bukhari, Al-Muslim',
        duration: '30 min',
        type: 'reflection',
        difficulty: 'Tafakkur',
        amanahDomain: 'Pedagogy as Amanah — Key 04 (Ilm) → Key 08 (Khalifah)',
      },
      {
        id: 'teach-2',
        title: 'Adamic Naming Exercise',
        prompt: '"He taught Adam the names — all of them." (Quran 2:31) Design a lesson where students name 5 things in their environment, then trace each name back to a Name of Allah. The naming is the first act (Adamic capacity). The fission is the second (recognition). Document what Names your students surface unprompted.',
        rolePromptVariants: {
          engineer: 'Name 5 components in your current system. Trace each name back to a Name of Allah. What Names are embedded in the engineering vocabulary you use daily?',
          teacher: 'Design the Adamic naming exercise for your students: name 5 things in the classroom, trace each to a Name of Allah. Document what Names surface unprompted.',
          farmer: 'Name 5 plants or soil elements in your garden. Trace each name back to a Name of Allah. What Names are embedded in the botanical vocabulary of your work?',
          'returning-citizen': 'Name 5 institutions or systems in your community. Trace each name back to a Name of Allah — or to a Name being violated. What Names are embedded in the language of reform?',
          scientist: 'Name 5 phenomena in your research. Trace each name back to a Name of Allah. What Names are embedded in the scientific vocabulary of your field?',
          custodian: 'Name 5 elements of the space you maintain. Trace each name back to a Name of Allah. What Names are embedded in the language of custodianship?',
        },
        lens: 'Adamic naming as Layer 1 — the IRL Lens bridges Adam\'s names (of things) to Allah\'s Names (of Attributes)',
        namesActive: ['Al-Alim', 'Al-Hakim', 'Al-Khaliq'],
        ayahAnchor: '"And He taught Adam the names — all of them. Then He displayed them to the angels."',
        ayahRef: 'Quran 2:31',
        duration: '60 min',
        type: 'action',
        difficulty: 'Tafakkur',
        amanahDomain: 'Naming as Khalifah capacity — Key 01 (Ayat) → Key 08 (Khalifah)',
      },
      {
        id: 'teach-3',
        title: 'Curriculum Fission',
        prompt: 'Take one lesson from your current curriculum. Run it through the IRL Lens: What Names of Allah are operative in the subject matter? What Sunnat Allah sequence does the content reveal? What Mizan principle does it teach? What Amanah does it prepare students for? Document the fission — you may be teaching more than you know.',
        rolePromptVariants: {
          engineer: 'Take one engineering specification or standard. Run it through the IRL Lens: What Names are operative in the standard? What Sunnat Allah does it encode? What Mizan does it protect?',
          teacher: 'Take one lesson from your curriculum. Run it through the IRL Lens: What Names are operative? What Sunnat Allah does the content reveal? What Amanah does it prepare students for?',
          farmer: 'Take one farming practice from your work. Run it through the IRL Lens: What Names are operative in this practice? What Sunnat Allah does it follow? What Mizan does it maintain?',
          'returning-citizen': 'Take one reform proposal from your work. Run it through the IRL Lens: What Names are operative in the reform? What Sunnat Allah does it restore? What Mizan does it repair?',
          scientist: 'Take one research methodology from your work. Run it through the IRL Lens: What Names are operative in the method? What Sunnat Allah does it reveal? What Taqdir does it measure?',
          custodian: 'Take one maintenance practice from your work. Run it through the IRL Lens: What Names are operative in this practice? What Sunnat Allah does it follow? What Amanah does it fulfill?',
        },
        lens: 'Teaching as recognition — the curriculum is already full of Names; the teacher\'s work is to see them',
        namesActive: ['Al-Alim', 'Al-Hakim', 'Al-Musawwir'],
        ayahAnchor: '"Whoever takes a path upon which to obtain knowledge, Allah makes the path to Paradise easy for him."',
        ayahRef: 'Jami at-Tirmidhi 2646',
        duration: '45 min',
        type: 'reflection',
        difficulty: 'Hikmah',
        amanahDomain: 'Curriculum as Amanah — Key 04 (Ilm) → Key 09 (Amanah)',
      },
    ],
  },
  {
    id: 'farmer',
    title: 'Farmer / Gardener',
    arabic: 'فَلَّاح',
    color: '#2d7a47',
    icon: '⬡',
    description: 'Soil and seasonal tasks — planting as Tafakkur, the earth as Amanah',
    amanahDomain: 'The earth as Amanah — "And not cause corruption in the earth after its reformation." Every seed planted is a trust.',
    quests: [
      {
        id: 'farm-1',
        title: 'Soil Reading',
        prompt: 'Take a handful of soil from your garden or a nearby green space. Observe: color, texture, smell, moisture, what lives in it. Apply the Three Questions: Is it? (what is its current state?) What is it? (what Names compose it?) When is/was it? (what seasonal Sunnat Allah is active right now?). Document which Names you recognize in the soil.',
        rolePromptVariants: {
          engineer: 'Read the soil as a system: what are its inputs (rain, nutrients, organisms), outputs (plant growth, water filtration), and feedback loops? Where is the Mizan in this system?',
          teacher: 'Read the soil as a curriculum: what does it teach about decomposition, renewal, patience? What Names of Allah are embedded in the soil\'s pedagogy?',
          farmer: 'Read the soil with your hands: color, texture, smell, moisture, life. Apply the Three Questions. Document which Names you recognize in this specific soil.',
          'returning-citizen': 'Read the soil as a Mizan indicator: is this land healthy or depleted? What human choices produced this soil state? What Islah does the soil require?',
          scientist: 'Read the soil as data: pH, organic matter, microbial activity, compaction. What Taqdir is encoded in these measurements? What Names are operative in the soil chemistry?',
          custodian: 'Read the soil as a space under your care: what does it need? What is thriving, what is struggling? What does custodianship of this soil require?',
        },
        lens: 'Hawas as soil reading — the senses are the first instruments of Taqdir recognition',
        namesActive: ['Al-Muhyi', 'Al-Qadir', 'Ar-Razzaq'],
        ayahAnchor: '"And the land — the pure comes forth its vegetation by permission of its Lord, but which is bad not come forth except difficulty."',
        ayahRef: 'Quran 7:58',
        duration: '20 min',
        type: 'observation',
        difficulty: 'Hawas',
        amanahDomain: 'Earth as Amanah — Key 06 (Fitra) → Key 07 (Mizan)',
      },
      {
        id: 'farm-2',
        title: 'Seasonal Sunnat Allah Map',
        prompt: 'What season are you in right now? Map the Sunnat Allah sequence active in your garden: what is germinating, growing, fruiting, composting, or dormant? For each stage, identify the Names of Allah operative in that process. "He causes to grow for you with it, the crops and the olives and the date palms and the grapes and of every kind fruits." (Quran 16:11) — which of these are in your ward?',
        rolePromptVariants: {
          engineer: 'Map the seasonal Sunnat Allah as a systems cycle: what are the inputs, processes, and outputs of each season? Where does the engineering of the ecosystem reveal Al-Musawwir?',
          teacher: 'Map the seasonal Sunnat Allah as a curriculum: what does each season teach? What Names are active in germination, growth, fruiting, composting? Design a lesson from the seasonal cycle.',
          farmer: 'Map the Sunnat Allah sequence active in your garden right now: what is germinating, growing, fruiting, composting, dormant? For each stage, identify the Names operative in that process.',
          'returning-citizen': 'Map the seasonal Sunnat Allah as a Mizan indicator: which communities have access to seasonal food? Where is the distribution of Ar-Razzaq\'s provision blocked?',
          scientist: 'Map the seasonal Sunnat Allah as measurable data: phenological timing, nutrient cycling rates, species activity. What Taqdir governs the seasonal sequence?',
          custodian: 'Map the seasonal Sunnat Allah in the spaces you maintain: what changes with each season? What custodianship does each season require?',
        },
        lens: 'Seasonal rhythm as Sunnat Allah — quests follow creation\'s rhythm, not the platform\'s content calendar',
        namesActive: ['Al-Muhyi', 'Al-Mumit', 'Ar-Razzaq', 'Al-Latif'],
        ayahAnchor: '"He causes to grow for you with it, the crops and the olives and the date palms and the grapes and of every kind fruits. Indeed in that surely a Sign for a people who reflect."',
        ayahRef: 'Quran 16:11',
        duration: '40 min',
        type: 'observation',
        difficulty: 'Tafakkur',
        amanahDomain: 'Seasonal stewardship — Key 07 (Mizan) → Key 09 (Amanah)',
      },
      {
        id: 'farm-3',
        title: 'Planting Niyyah',
        prompt: 'Before planting today, set a Niyyah: what are you planting, and why? Which Ayat, Signs are you pondering while doing so? "And not cause corruption in the earth after its reformation. And call Him in fear and hope." (Quran 7:56) — how does your planting serve the Mizan? Document the Niyyah before you begin, and the recognition after you finish.',
        rolePromptVariants: {
          engineer: 'Set a Niyyah before your next design session: what are you building, and why? Which Names of Allah are you serving in this design? Document the Niyyah before you begin and the recognition after.',
          teacher: 'Set a Niyyah before your next lesson: what are you teaching, and why? Which Names of Allah are active in this curriculum? Document the Niyyah before you begin and the recognition after.',
          farmer: 'Set a Niyyah before planting: what are you planting, and why? Which Ayat are you pondering while doing so? Document the Niyyah before you begin and the recognition after you finish.',
          'returning-citizen': 'Set a Niyyah before your next advocacy action: what are you working toward, and why? Which Names of Allah are active in this Islah? Document the Niyyah before you begin.',
          scientist: 'Set a Niyyah before your next research session: what are you looking for, and why? Which Names of Allah are you serving in this research? Document the Niyyah before you begin.',
          custodian: 'Set a Niyyah before your next maintenance shift: what are you caring for, and why? Which Names of Allah are active in this custodianship? Document the Niyyah before you begin.',
        },
        lens: 'Planting as Tafakkur — every seed is a trust; the gardener\'s Niyyah shapes the Amanah',
        namesActive: ['Al-Qadir', 'Al-Khaliq', 'Al-Muhyi'],
        ayahAnchor: '"And not cause corruption in the earth after its reformation. And call Him in fear and hope."',
        ayahRef: 'Quran 7:56',
        duration: '60 min',
        type: 'action',
        difficulty: 'Tafakkur',
        amanahDomain: 'Niyyah as Amanah foundation — Key 06 (Fitra) → Key 09 (Amanah)',
      },
    ],
  },
  {
    id: 'returning-citizen',
    title: 'Returning Citizen',
    arabic: 'عَائِد',
    color: '#a85c2d',
    icon: '◈',
    description: 'Mizan restoration — map the transgressions, propose the Islah, work the reform',
    amanahDomain: 'Islah as vocation — "Whosoever of you sees an evil, let him change it." The returning citizen sees what others overlook.',
    quests: [
      {
        id: 'rc-1',
        title: 'Seven Barriers Mizan Map',
        prompt: 'Identify the 7 most significant barriers to reentry in your ward (housing, employment, ID, banking, transportation, healthcare, community connection — or your own list). For each barrier: Is it a Mizan transgression? What Names of Allah does it violate? What would Islah look like for this specific barrier? "Whosoever of you sees an evil, let him change it with his hand." — what can you change with your hand today?',
        rolePromptVariants: {
          engineer: 'Map the 7 barriers as infrastructure failures: which systems are broken, which are absent, which are actively hostile? What engineering solutions does each barrier require?',
          teacher: 'Map the 7 barriers as educational gaps: which barriers could be addressed through curriculum, training, or knowledge transfer? What does the rain hadith say about each barrier?',
          farmer: 'Map the 7 barriers as soil conditions: which barriers are compacted (resistant), which are depleted (lacking nutrients), which are contaminated (actively harmful)? What remediation does each require?',
          'returning-citizen': 'Map the 7 barriers you have personally encountered or witnessed. For each: Is it a Mizan transgression? What Names does it violate? What would Islah look like?',
          scientist: 'Map the 7 barriers as measurable data: what are the quantifiable impacts of each barrier? What Taqdir is encoded in these measurements? What does Al-Adl require in response?',
          custodian: 'Map the 7 barriers as spaces that need custodianship: which barriers are about neglected systems, abandoned spaces, or unmaintained infrastructure? What custodianship does each require?',
        },
        lens: 'Mizan as measurable — the balance is observable; the returning citizen sees what others overlook',
        namesActive: ['Al-Adl', 'Al-Qadir', 'Al-Wali'],
        ayahAnchor: '"Whosoever of you sees an evil, let him change it with his hand; and if he is not able to do so, then with his tongue; and if he is not able to do so, then with his heart — that is the weakest of faith."',
        ayahRef: 'Sahih Muslim 49a',
        duration: '60 min',
        type: 'mizan',
        difficulty: 'Tafakkur',
        amanahDomain: 'Mizan restoration — Key 07 (Mizan) → Key 08 (Amanah)',
      },
      {
        id: 'rc-2',
        title: 'Islah Reform Proposal',
        prompt: 'Choose one community institution (a school, a housing authority, a parole office, a mosque, a community center). Write a 1-page Islah proposal: What is broken? What does the Mizan require? What specific reforms would restore the balance? What are the core tenets of your reasoning? Ground your proposal in at least one Quranic principle and one observable community condition.',
        rolePromptVariants: {
          engineer: 'Write an Islah proposal for one infrastructure system in your ward: what is broken, what does the Mizan require, what specific engineering reforms would restore the balance?',
          teacher: 'Write an Islah proposal for one educational institution: what is broken in the curriculum or pedagogy, what does the Mizan require, what specific reforms would restore the balance?',
          farmer: 'Write an Islah proposal for one land-use policy in your ward: what is broken in how the land is managed, what does the Mizan require, what specific reforms would restore the balance?',
          'returning-citizen': 'Write an Islah proposal for one community institution you have direct experience with: what is broken, what does the Mizan require, what specific reforms would restore the balance?',
          scientist: 'Write an Islah proposal grounded in your research data: what does the evidence show is broken, what does the Mizan require, what specific reforms does the data support?',
          custodian: 'Write an Islah proposal for one space under your custodianship: what is broken, what does the Mizan require, what specific reforms would restore the balance?',
        },
        lens: 'Islah as vocation — "to cultivate, mend, restore, renew, work righteousness" — the bootstrapped OS needs a complete fix',
        namesActive: ['Al-Adl', 'Al-Hakim', 'Al-Wali'],
        ayahAnchor: '"The word and work we\'re looking for is Islah, to cultivate, mend, restore, renew, work righteousness."',
        ayahRef: 'Open Letter — Islah Section',
        duration: '90 min',
        type: 'restoration',
        difficulty: 'Hikmah',
        amanahDomain: 'Islah as Khalifah work — Key 08 (Khalifah) → Key 09 (Amanah)',
      },
      {
        id: 'rc-3',
        title: 'Mutual Aid Mycorrhizal Map',
        prompt: 'Map the mutual aid networks in your ward. Who is sharing resources? Who is isolated? Where are the mycorrhizal connections — the invisible threads of care that sustain the community? "Al-Wali in the network. We are learning from the earth." Identify 3 connections you could strengthen or create. What would it look like if this network were oriented toward the remembrance of Allah?',
        rolePromptVariants: {
          engineer: 'Map the mutual aid networks as infrastructure: what are the nodes, connections, and failure points? Where are the redundancies? Where are the single points of failure?',
          teacher: 'Map the mutual aid networks as a curriculum: what knowledge is being shared, what is being withheld, what needs to be taught? How does the rain hadith apply to this network?',
          farmer: 'Map the mutual aid networks as a mycorrhizal system: who is sharing nutrients, who is isolated, who is being protected? What does the Three Sisters planting teach about this network?',
          'returning-citizen': 'Map the mutual aid networks in your ward: who is sharing resources, who is isolated, where are the invisible threads of care? Identify 3 connections you could strengthen or create.',
          scientist: 'Map the mutual aid networks as measurable data: what are the resource flows, the connection densities, the isolated nodes? What Taqdir is encoded in this network structure?',
          custodian: 'Map the mutual aid networks as spaces of care: which spaces host sharing and connection, which are isolated, which need custodianship to become gathering places?',
        },
        lens: 'Mutual aid as Sunnat Allah — the mycorrhizal network in the soil does what we\'re trying to do',
        namesActive: ['Al-Wali', 'Ar-Razzaq', 'Al-Latif'],
        ayahAnchor: '"Mutual aid is Sunnat Allah in community form. The mycorrhizal network in the soil does what we\'re trying to do — share resources, communicate need, protect the vulnerable."',
        ayahRef: 'Open Letter — Vocational Network',
        duration: '45 min',
        type: 'action',
        difficulty: 'Tafakkur',
        amanahDomain: 'Community as Amanah — Key 09 (Amanah) → Key 08 (Khalifah)',
      },
    ],
  },
  {
    id: 'scientist',
    title: 'Scientist',
    arabic: 'عَالِم',
    color: '#8a8ab8',
    icon: '◎',
    description: 'Research as fission — what Names are operative in the phenomenon you study?',
    amanahDomain: 'Discovery as recognition — "You recognized what the Pen wrote." Research uncovers what was already decreed.',
    quests: [
      {
        id: 'sci-1',
        title: 'Phenomenon Fission',
        prompt: 'Choose one phenomenon you are currently studying or observing. Run it through the full IRL Lens fission: What Names of Allah are operative in this phenomenon? What Sunnat Allah sequence does it reveal? What Taqdir (measure) governs it? "You recognized what the Pen wrote" — what was already written in this phenomenon before you began studying it?',
        rolePromptVariants: {
          engineer: 'Choose one engineering phenomenon (stress distribution, heat transfer, signal propagation). Run it through the IRL Lens: What Names are operative? What Sunnat Allah sequence does it reveal?',
          teacher: 'Choose one learning phenomenon (attention, retention, transfer). Run it through the IRL Lens: What Names are operative in how students learn? What Sunnat Allah governs the process?',
          farmer: 'Choose one ecological phenomenon (photosynthesis, decomposition, pollination). Run it through the IRL Lens: What Names are operative? What Sunnat Allah sequence does it reveal?',
          'returning-citizen': 'Choose one social phenomenon (recidivism, community resilience, mutual aid). Run it through the IRL Lens: What Names are operative? What Sunnat Allah sequence does it reveal?',
          scientist: 'Choose one phenomenon from your research. Run it through the full IRL Lens fission: What Names are operative? What Sunnat Allah sequence does it reveal? What was already written in this phenomenon?',
          custodian: 'Choose one maintenance phenomenon (rust, growth, wear, renewal). Run it through the IRL Lens: What Names are operative? What Sunnat Allah sequence does it reveal?',
        },
        lens: 'Research as recognition — the scientist uncovers what was decreed, not what they invented',
        namesActive: ['Al-Alim', 'Al-Khabir', 'Al-Qadir'],
        ayahAnchor: '"This recognition was decreed. You recognized what the Pen wrote. Your observation adds to the communal tafsir of the Qadr."',
        ayahRef: 'Open Letter — Observation Complete',
        duration: '45 min',
        type: 'observation',
        difficulty: 'Hikmah',
        amanahDomain: 'Research as Amanah — Key 02 (Tafakkur) → Key 05 (Sunnat Allah)',
      },
      {
        id: 'sci-2',
        title: 'Mizan Data Collection',
        prompt: 'Identify one measurable Mizan indicator in your ward (air quality, soil health, food access, green space, water quality). Collect baseline data. Frame the data as Taqdir: "Every numerical metric in the garden is a taqdir — decree made measurable — not a neutral statistic." What does Al-Adl require in response to this measurement?',
        rolePromptVariants: {
          engineer: 'Identify one measurable infrastructure Mizan indicator (load capacity, energy efficiency, water pressure). Collect baseline data. Frame it as Taqdir. What does Al-Adl require in response?',
          teacher: 'Identify one measurable educational Mizan indicator (literacy rates, graduation rates, resource access). Collect baseline data. Frame it as Taqdir. What does Al-Adl require in response?',
          farmer: 'Identify one measurable ecological Mizan indicator (soil pH, biodiversity index, water retention). Collect baseline data. Frame it as Taqdir. What does Al-Adl require in response?',
          'returning-citizen': 'Identify one measurable justice Mizan indicator (recidivism rates, reentry success rates, resource access gaps). Collect baseline data. Frame it as Taqdir. What does Al-Adl require?',
          scientist: 'Identify one measurable Mizan indicator in your ward. Collect baseline data. Frame the data as Taqdir — decree made measurable. What does Al-Adl require in response to this measurement?',
          custodian: 'Identify one measurable custodianship Mizan indicator (cleanliness index, maintenance response time, habitat health). Collect baseline data. Frame it as Taqdir. What does Al-Adl require?',
        },
        lens: 'Data as Taqdir — every metric is decree made measurable; the scientist reads the Qadr',
        namesActive: ['Al-Adl', 'Al-Qadir', 'Al-Khabir'],
        ayahAnchor: '"And in the earth Ayat (Signs) for those who are certain."',
        ayahRef: 'Quran 51:20',
        duration: '60 min',
        type: 'observation',
        difficulty: 'Tafakkur',
        amanahDomain: 'Data as Taqdir — Key 05 (Sunnat Allah) → Key 10 (Haqq)',
      },
      {
        id: 'sci-3',
        title: 'Breakthrough Research Niyyah',
        prompt: 'What breakthrough research motivates you? Write a Niyyah for your research: What are you looking for? What Names of Allah are you serving in your methodology — whether you know it or not? "If your research were oriented toward the remembrance of Allah, what would you be looking for?" Set the Niyyah before your next research session.',
        rolePromptVariants: {
          engineer: 'Write a Niyyah for your next engineering project: what are you building, what Names are you serving, what Amanah does this project carry? Set the Niyyah before you begin.',
          teacher: 'Write a Niyyah for your next curriculum unit: what are you teaching, what Names are you transmitting, what Amanah does this knowledge carry? Set the Niyyah before you begin.',
          farmer: 'Write a Niyyah for your next planting season: what are you growing, what Names are you serving, what Amanah does this cultivation carry? Set the Niyyah before you begin.',
          'returning-citizen': 'Write a Niyyah for your next advocacy action: what are you working toward, what Names are you serving, what Amanah does this Islah carry? Set the Niyyah before you begin.',
          scientist: 'Write a Niyyah for your next research session: what are you looking for, what Names are you serving, what Amanah does this research carry? Set the Niyyah before you begin.',
          custodian: 'Write a Niyyah for your next maintenance shift: what are you caring for, what Names are you serving, what Amanah does this custodianship carry? Set the Niyyah before you begin.',
        },
        lens: 'Research Niyyah — the scientist\'s intention shapes what they recognize in the data',
        namesActive: ['Al-Alim', 'Al-Hakim', 'Al-Baseer'],
        ayahAnchor: '"Be mindful of Allah and Allah will protect you. Be mindful of Allah, and you will find Him in front of you."',
        ayahRef: 'Hadith 19, 40 Hadith an-Nawawi',
        duration: '15 min',
        type: 'reflection',
        difficulty: 'Hawas',
        amanahDomain: 'Niyyah as research foundation — Key 02 (Tafakkur) → Key 10 (Haqq)',
      },
    ],
  },
  {
    id: 'custodian',
    title: 'Custodian / Janitor',
    arabic: 'حَارِس',
    color: '#5a7a4a',
    icon: '⬡',
    description: 'Custodianship of endangered habitats — the janitor as ecological guardian',
    amanahDomain: 'Custodianship as Khalifah — "Indeed, I am going to place in the earth a caretaker." The janitor is the most direct expression of this trust.',
    quests: [
      {
        id: 'cust-1',
        title: 'Habitat Custodianship Walk',
        prompt: 'Walk through the space you maintain. Look for endangered or neglected ecological elements: a crack in the pavement where a plant grows, a corner where water pools, a surface where lichen has established. "How would a janitor approach the custodianship of endangered habitats?" Document 3 micro-habitats in your space. What Names of Allah are active in each?',
        rolePromptVariants: {
          engineer: 'Walk through an infrastructure system you maintain or design. Look for endangered or neglected elements: a failing component, an overlooked connection, a stressed node. Document 3 micro-failures. What Names are active in each?',
          teacher: 'Walk through your classroom or school. Look for endangered or neglected learning elements: a student who is isolated, a concept that is misunderstood, a resource that is unused. Document 3 micro-habitats of learning. What Names are active in each?',
          farmer: 'Walk through your garden. Look for endangered or neglected ecological elements: a struggling plant, a depleted soil patch, a disrupted water flow. Document 3 micro-habitats. What Names are active in each?',
          'returning-citizen': 'Walk through your ward. Look for endangered or neglected community elements: an abandoned space, an isolated person, a broken institution. Document 3 micro-habitats of community. What Names are active in each?',
          scientist: 'Walk through your research environment. Look for endangered or neglected data: an overlooked variable, a dismissed anomaly, an unexamined assumption. Document 3 micro-habitats of inquiry. What Names are active in each?',
          custodian: 'Walk through the space you maintain. Look for endangered or neglected ecological elements: a crack where a plant grows, a corner where water pools, a surface where lichen has established. Document 3 micro-habitats. What Names are active in each?',
        },
        lens: 'Custodianship as Khalifah — the janitor is the most direct expression of caretaking in the built environment',
        namesActive: ['Al-Muhyi', 'Al-Hafiz', 'Al-Latif'],
        ayahAnchor: '"And not cause corruption in the earth after its reformation."',
        ayahRef: 'Quran 7:56',
        duration: '30 min',
        type: 'observation',
        difficulty: 'Hawas',
        amanahDomain: 'Space as Amanah — Key 09 (Amanah) → Key 08 (Khalifah)',
      },
      {
        id: 'cust-2',
        title: 'Decomposition Lens',
        prompt: 'Find a site of decomposition in your environment (fallen leaves, organic waste, a compost pile, a decaying structure). Observe: what is breaking down? What is being returned? What will grow from this? "A divine system ensuring nothing is wasted in creation." — Al-Muhyi and Al-Mumit in the same process. Document the Names active in the decomposition.',
        rolePromptVariants: {
          engineer: 'Find a site of material degradation in your engineering environment (rust, fatigue, wear). Observe: what is breaking down, what is being returned, what will be rebuilt? Al-Muhyi and Al-Mumit in the same process.',
          teacher: 'Find a site of conceptual decomposition in your teaching (a misconception being corrected, a habit being unlearned, an assumption being challenged). What is breaking down? What will grow from this?',
          farmer: 'Find a site of decomposition in your garden (fallen leaves, compost, decaying wood). Observe: what is breaking down, what is being returned, what will grow? Document the Names active.',
          'returning-citizen': 'Find a site of institutional decomposition in your community (a failing system, a collapsing structure, a dissolving norm). What is breaking down? What is being returned? What will grow from this?',
          scientist: 'Find a site of decomposition in your research (a disproven hypothesis, a superseded theory, an abandoned methodology). What is breaking down? What is being returned? What will grow from this?',
          custodian: 'Find a site of decomposition in the space you maintain (fallen leaves, organic waste, a decaying structure). Observe: what is breaking down, what is being returned, what will grow? Document the Names active.',
        },
        lens: 'Decomposition as Sunnat Allah — the custodian reads the Names in what is being returned',
        namesActive: ['Al-Muhyi', 'Al-Mumit', 'Ar-Razzaq'],
        ayahAnchor: '"This is the Sunnat Way of Allah, Al-Muhyyi; The Bestower of Life, Al-Mumit; The Bringer of Death, decomposition — a divine system ensuring nothing is wasted in creation."',
        ayahRef: 'Open Letter — Haal Platform Narrative',
        duration: '25 min',
        type: 'observation',
        difficulty: 'Tafakkur',
        amanahDomain: 'Renewal as Amanah — Key 05 (Sunnat Allah) → Key 07 (Mizan)',
      },
    ],
  },
];

// ─── YAQEEN LEVEL SELECTOR ────────────────────────────────────────────────────

const YAQEEN_LEVELS = [
  { id: 'Ilm', label: 'Ilm', arabic: 'عِلْم', desc: 'Knowledge — intellectual understanding of what was observed', color: '#8a8ab8' },
  { id: 'Ayn', label: 'Ayn', arabic: 'عَيْن', desc: 'Certainty of sight — you have witnessed this directly', color: '#c9a84c' },
  { id: 'Haqq', label: 'Haqq', arabic: 'حَقّ', desc: 'Certainty of reality — this truth has settled in the heart', color: '#2d7a47' },
];

function YaqeenSelector({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>
        Yaqeen Level — Certainty of this Observation
      </p>
      <div className="flex gap-2">
        {YAQEEN_LEVELS.map(y => (
          <button
            key={y.id}
            onClick={() => onChange(y.id)}
            title={y.desc}
            className="flex-1 rounded-lg py-2 px-2 transition-all"
            style={{
              background: value === y.id ? `${y.color}18` : 'rgba(255,255,255,0.03)',
              border: `1px solid ${value === y.id ? y.color + '60' : 'rgba(255,255,255,0.06)'}`,
              cursor: 'pointer',
            }}
          >
            <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color: value === y.id ? y.color : 'rgba(212,197,169,0.35)' }}>{y.label}</div>
            <div style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: value === y.id ? `${y.color}80` : 'rgba(212,197,169,0.2)', direction: 'rtl' }}>{y.arabic}</div>
          </button>
        ))}
      </div>
      {value && (
        <p className="font-mono text-[8px] mt-1.5" style={{ color: 'rgba(212,197,169,0.3)' }}>
          {YAQEEN_LEVELS.find(y => y.id === value)?.desc}
        </p>
      )}
    </div>
  );
}

// ─── NAMES QUICK TOGGLE ───────────────────────────────────────────────────────

const ALL_QUICK_NAMES = [
  'Al-Qadir', 'Al-Khaliq', 'Al-Musawwir', 'Ar-Razzaq', 'Al-Latif',
  'Al-Alim', 'Al-Hakim', 'Al-Adl', 'Al-Muhyi', 'Al-Mumit',
  'An-Nur', 'Al-Baseer', 'Al-Khabir', 'Al-Wali', 'Al-Haqq',
  'Al-Hafiz', 'Al-Wahhab', 'Al-Bari', 'Al-Jami', 'Al-Hadi',
];

function NamesQuickToggle({ active, preSelected, onChange }: { active: string[]; preSelected: string[]; onChange: (names: string[]) => void }) {
  const [showAll, setShowAll] = useState(false);
  const displayNames = showAll ? ALL_QUICK_NAMES : [...new Set([...preSelected, ...ALL_QUICK_NAMES.slice(0, 10)])];

  const toggle = (name: string) => {
    onChange(active.includes(name) ? active.filter(n => n !== name) : [...active, name]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>
          Names Extraction — Quick Toggle
        </p>
        <button
          onClick={() => setShowAll(!showAll)}
          className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded"
          style={{ background: 'rgba(201,168,76,0.06)', color: 'rgba(201,168,76,0.5)', border: '1px solid rgba(201,168,76,0.12)' }}
        >
          {showAll ? 'Show Less' : 'Show All'}
        </button>
      </div>
      {preSelected.length > 0 && (
        <p className="font-mono text-[7px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.4)' }}>
          ✦ Pre-selected by vocational lens
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {displayNames.map(name => {
          const isPreSelected = preSelected.includes(name);
          const isActive = active.includes(name);
          return (
            <button
              key={name}
              onClick={() => toggle(name)}
              className="font-mono text-[8px] px-2 py-1 rounded transition-all"
              style={{
                background: isActive ? 'rgba(201,122,71,0.15)' : isPreSelected ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isActive ? 'rgba(201,122,71,0.5)' : isPreSelected ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.06)'}`,
                color: isActive ? '#c9a84c' : isPreSelected ? 'rgba(201,168,76,0.6)' : 'rgba(212,197,169,0.35)',
              }}
            >
              {isPreSelected && !isActive ? '◦ ' : isActive ? '✦ ' : ''}{name}
            </button>
          );
        })}
      </div>
      {active.length > 0 && (
        <p className="font-mono text-[8px] mt-2" style={{ color: 'rgba(201,122,71,0.5)' }}>
          {active.length} Name{active.length !== 1 ? 's' : ''} extracted: {active.join(', ')}
        </p>
      )}
    </div>
  );
}

// ─── QUEST CARD ───────────────────────────────────────────────────────────────

function QuestCard({
  quest,
  color,
  activeLensId,
  amanahDomain,
  onComplete,
}: {
  quest: Quest;
  color: string;
  activeLensId: string;
  amanahDomain: string;
  onComplete: (id: string, observation: CompletedQuestObservation) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showObsForm, setShowObsForm] = useState(false);
  const [yaqeenLevel, setYaqeenLevel] = useState('Ilm');
  const [extractedNames, setExtractedNames] = useState<string[]>(quest.namesActive);
  const [obsText, setObsText] = useState('');
  const [obsSubmitted, setObsSubmitted] = useState(false);
  const [showRoleVariant, setShowRoleVariant] = useState(false);

  const difficultyColors: Record<string, string> = { Hawas: '#2d7a47', Tafakkur: '#c9a84c', Hikmah: '#8a8ab8' };
  const typeIcons: Record<string, string> = { observation: '◎', reflection: '✦', action: '⬡', mizan: '◈', restoration: '⌘' };

  const roleVariant = quest.rolePromptVariants?.[activeLensId];
  const effectivePrompt = showRoleVariant && roleVariant ? roleVariant : quest.prompt;

  // Pre-fill observation form with role context auto-populated
  const buildPreFilledObs = () => {
    const roleLabel = activeLensId.charAt(0).toUpperCase() + activeLensId.slice(1).replace('-', ' ');
    return `Quest: ${quest.title}
Vocational Lens: ${roleLabel}
Yaqeen Level: ${yaqeenLevel}
Names Active: ${extractedNames.join(', ')}
Amanah Domain: ${quest.amanahDomain || amanahDomain}
Ayah Anchor: ${quest.ayahAnchor}

My Observation:
`;
  };

  const handleOpenObsForm = () => {
    setObsText(buildPreFilledObs());
    setShowObsForm(true);
  };

  const handleObsSubmit = () => {
    if (obsText.trim()) {
      const observation: CompletedQuestObservation = {
        questId: quest.id,
        questTitle: quest.title,
        lensId: activeLensId,
        yaqeenLevel: yaqeenLevel as 'Ilm' | 'Ayn' | 'Haqq',
        namesExtracted: extractedNames,
        observationText: obsText,
        amanahDomain: quest.amanahDomain || amanahDomain,
        timestamp: new Date().toISOString(),
      };
      setObsSubmitted(true);
      setShowObsForm(false);
      setCompleted(true);
      onComplete(quest.id, observation);
    }
  };

  const handleMarkComplete = () => {
    const observation: CompletedQuestObservation = {
      questId: quest.id,
      questTitle: quest.title,
      lensId: activeLensId,
      yaqeenLevel: yaqeenLevel as 'Ilm' | 'Ayn' | 'Haqq',
      namesExtracted: extractedNames,
      observationText: '',
      amanahDomain: quest.amanahDomain || amanahDomain,
      timestamp: new Date().toISOString(),
    };
    setCompleted(true);
    onComplete(quest.id, observation);
  };

  return (
    <div className="rounded-xl overflow-hidden transition-all" style={{ background: completed ? 'rgba(45,122,71,0.06)' : `${color}06`, border: `1px solid ${completed ? 'rgba(45,122,71,0.3)' : color + '20'}` }}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span style={{ color, fontSize: 14 }}>{typeIcons[quest.type]}</span>
            <h4 className="font-mono text-xs font-black uppercase tracking-widest" style={{ color: completed ? 'rgba(45,122,71,0.8)' : '#F5F0E8' }}>{quest.title}</h4>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[8px] px-2 py-0.5 rounded" style={{ background: `${difficultyColors[quest.difficulty]}15`, color: difficultyColors[quest.difficulty], border: `1px solid ${difficultyColors[quest.difficulty]}25` }}>{quest.difficulty}</span>
            <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.3)' }}>{quest.duration}</span>
          </div>
        </div>

        {/* Role variant toggle */}
        {roleVariant && (
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => setShowRoleVariant(!showRoleVariant)}
              className="font-mono text-[8px] uppercase tracking-widest px-2 py-1 rounded transition-all"
              style={{
                background: showRoleVariant ? `${color}15` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${showRoleVariant ? color + '40' : 'rgba(255,255,255,0.06)'}`,
                color: showRoleVariant ? color : 'rgba(212,197,169,0.35)',
              }}
            >
              {showRoleVariant ? '✦ Role Variant Active' : '◦ Show Role-Specific Prompt'}
            </button>
          </div>
        )}

        <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>
          {expanded ? effectivePrompt : effectivePrompt.slice(0, 120) + '...'}
        </p>

        {expanded && (
          <div className="space-y-4 mb-4">
            {/* Ayah Anchor */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(45,122,71,0.5)' }}>Ayah Anchor</p>
              <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.6, fontStyle: 'italic' }}>{quest.ayahAnchor}</p>
              <p className="font-mono text-[8px] mt-1" style={{ color: 'rgba(212,197,169,0.3)' }}>{quest.ayahRef}</p>
            </div>

            {/* Vocational Lens */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.12)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>Vocational Lens</p>
              <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.6 }}>{quest.lens}</p>
            </div>

            {/* Amanah Domain — auto-populated by lens */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(138,138,184,0.05)', border: '1px solid rgba(138,138,184,0.15)' }}>
              <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(138,138,184,0.5)' }}>Amanah Domain</p>
              <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 12, lineHeight: 1.6 }}>{quest.amanahDomain || amanahDomain}</p>
            </div>

            {/* Names Quick Toggle — pre-selected by lens */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <NamesQuickToggle
                active={extractedNames}
                preSelected={quest.namesActive}
                onChange={setExtractedNames}
              />
            </div>

            {/* Yaqeen Level Selector */}
            <div className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <YaqeenSelector value={yaqeenLevel} onChange={setYaqeenLevel} />
            </div>

            {/* Observation Pre-Form */}
            {!completed && (
              <div>
                {!showObsForm ? (
                  <button
                    onClick={handleOpenObsForm}
                    className="w-full font-mono text-[9px] uppercase tracking-widest py-2.5 rounded-lg transition-all text-left px-3"
                    style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)', color: 'rgba(45,138,135,0.7)' }}
                  >
                    ◎ Open Observation Pre-Form — Role Context Auto-Filled →
                  </button>
                ) : (
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(45,138,135,0.3)' }}>
                    <div className="px-4 py-3" style={{ background: 'rgba(45,138,135,0.08)', borderBottom: '1px solid rgba(45,138,135,0.15)' }}>
                      <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(45,138,135,0.7)' }}>
                        ◎ Observation Pre-Form · Yaqeen: {yaqeenLevel} · Names: {extractedNames.length} extracted
                      </p>
                    </div>
                    <div className="p-4">
                      <textarea
                        value={obsText}
                        onChange={e => setObsText(e.target.value)}
                        rows={9}
                        className="w-full rounded-lg p-3 font-mono text-[11px] resize-none focus:outline-none"
                        style={{
                          background: 'rgba(6,13,18,0.8)',
                          border: '1px solid rgba(45,138,135,0.2)',
                          color: 'rgba(212,197,169,0.75)',
                          lineHeight: 1.7,
                        }}
                        placeholder="Add your observation here..."
                      />
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={handleObsSubmit}
                          className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded transition-all"
                          style={{ background: 'rgba(45,138,135,0.15)', color: '#2d8a87', border: '1px solid rgba(45,138,135,0.35)' }}
                        >
                          Submit & Mark Complete
                        </button>
                        <button
                          onClick={() => setShowObsForm(false)}
                          className="font-mono text-[9px] uppercase tracking-widest px-3 py-2 rounded transition-all"
                          style={{ background: 'transparent', color: 'rgba(212,197,169,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Amanah Tracker Link */}
            <a
              href="/user-dashboard?tab=garment"
              className="flex items-center gap-2 rounded-lg p-3 transition-all"
              style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', textDecoration: 'none' }}
            >
              <span style={{ color: '#c9a84c', fontSize: 12 }}>⬡</span>
              <div>
                <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(201,168,76,0.6)' }}>Amanah Tracker</p>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11 }}>Completed quests weave into your Amanah record — view your garment threads</p>
              </div>
              <span style={{ color: 'rgba(201,168,76,0.3)', fontSize: 10, marginLeft: 'auto' }}>→</span>
            </a>
          </div>
        )}

        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(212,197,169,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            {expanded ? 'Collapse' : 'Expand Quest'}
          </button>
          {!completed ? (
            <button
              onClick={handleMarkComplete}
              className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
              style={{ background: `${color}12`, color, border: `1px solid ${color}30` }}
            >
              Mark Complete
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded" style={{ background: 'rgba(45,122,71,0.1)', color: 'rgba(45,122,71,0.8)', border: '1px solid rgba(45,122,71,0.25)' }}>
                ✓ Completed
              </span>
              <a
                href="/user-dashboard?tab=garment"
                className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 rounded transition-all"
                style={{ background: 'rgba(201,168,76,0.08)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.2)', textDecoration: 'none' }}
              >
                → Amanah Tracker
              </a>
            </div>
          )}
        </div>

        {obsSubmitted && (
          <div className="mt-3 rounded-lg p-3" style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)' }}>
            <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(45,122,71,0.7)' }}>
              ✓ Observation recorded — "This recognition was decreed. You recognized what the Pen wrote."
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMPLETED QUEST OBSERVATION TYPE ─────────────────────────────────────────

export interface CompletedQuestObservation {
  questId: string;
  questTitle: string;
  lensId: string;
  yaqeenLevel: 'Ilm' | 'Ayn' | 'Haqq';
  namesExtracted: string[];
  observationText: string;
  amanahDomain: string;
  timestamp: string;
}

// ─── ROLE-TO-LENS MAPPING ─────────────────────────────────────────────────────

const ROLE_TO_LENS: Record<string, string> = {
  'community-caretaker': 'custodian',
  'inner-seeker': 'scientist',
  'academic': 'teacher',
  'activist': 'returning-citizen',
  'explorer': 'farmer',
  'local-resource': 'engineer',
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function DailyQuestGenerator({
  initialRole,
  onQuestComplete,
}: {
  initialRole?: string;
  onQuestComplete?: (obs: CompletedQuestObservation) => void;
} = {}) {
  const [activeLensId, setActiveLensId] = useState(() => {
    if (typeof window !== 'undefined') {
      if (initialRole && ROLE_TO_LENS[initialRole]) return ROLE_TO_LENS[initialRole];
      return localStorage.getItem('haal_active_vocation') || 'engineer';
    }
    if (initialRole && ROLE_TO_LENS[initialRole]) return ROLE_TO_LENS[initialRole];
    return 'engineer';
  });
  const [completedQuests, setCompletedQuests] = useState<string[]>([]);
  const [completedObservations, setCompletedObservations] = useState<CompletedQuestObservation[]>([]);
  const [dailyDate] = useState(() => new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }));

  const handleLensChange = (id: string) => {
    setActiveLensId(id);
    if (typeof window !== 'undefined') {
      localStorage.setItem('haal_active_vocation', id);
    }
  };

  const activeLens = VOCATIONAL_QUESTS.find(l => l.id === activeLensId) || VOCATIONAL_QUESTS[0];

  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  const dailyQuestIndex = dayOfYear % activeLens.quests.length;
  const dailyQuest = activeLens.quests[dailyQuestIndex];

  const handleComplete = (id: string, observation: CompletedQuestObservation) => {
    setCompletedQuests(prev => [...prev, id]);
    setCompletedObservations(prev => [...prev, observation]);
    // Persist to localStorage for VocationalNetwork to read
    if (typeof window !== 'undefined') {
      const existing = JSON.parse(localStorage.getItem('haal_completed_quest_observations') || '[]');
      localStorage.setItem('haal_completed_quest_observations', JSON.stringify([...existing, observation]));
    }
    onQuestComplete?.(observation);
  };

  return (
    <section id="section-daily-quests" style={{ background: 'rgba(8,16,24,0.98)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="mb-10">
          <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>
            ✦ Daily Quest Generator · Vocational Lens
          </p>
          <h2 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-3" style={{ color: '#F5F0E8' }}>
            What Work Are You Doing Today?
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 14, lineHeight: 1.75, maxWidth: 600, marginBottom: 8 }}>
            Daily prompts surface based on your active vocational lens. Each quest includes role-specific prompt variants, a Yaqeen-level selector, Names extraction quick-toggle, and Amanah domain — all auto-populated by your active lens. Mark complete to weave a thread into your Amanah tracker.
          </p>
          <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.25)' }}>{dailyDate}</p>
        </div>

        {/* Lens Selector */}
        <div className="mb-8">
          <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Active Vocational Lens</p>
          <div className="flex flex-wrap gap-2">
            {VOCATIONAL_QUESTS.map(lens => (
              <button
                key={lens.id}
                onClick={() => handleLensChange(lens.id)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all font-mono text-[10px] uppercase tracking-widest"
                style={{
                  background: activeLensId === lens.id ? `${lens.color}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${activeLensId === lens.id ? lens.color + '50' : 'rgba(255,255,255,0.06)'}`,
                  color: activeLensId === lens.id ? lens.color : 'rgba(212,197,169,0.35)',
                }}
              >
                <span>{lens.icon}</span>
                <span>{lens.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Lens Description + Amanah Domain */}
        <div className="rounded-xl p-5 mb-8" style={{ background: `${activeLens.color}08`, border: `1px solid ${activeLens.color}25` }}>
          <div className="flex items-center gap-3 mb-2">
            <span style={{ fontSize: 20, color: activeLens.color }}>{activeLens.icon}</span>
            <div>
              <span className="font-mono text-sm font-black uppercase tracking-widest" style={{ color: '#F5F0E8' }}>{activeLens.title}</span>
              <span style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${activeLens.color}60`, direction: 'rtl', marginLeft: 12 }}>{activeLens.arabic}</span>
            </div>
          </div>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 13, lineHeight: 1.65, marginBottom: 8 }}>{activeLens.description}</p>
          <div className="rounded-lg p-3" style={{ background: 'rgba(138,138,184,0.06)', border: '1px solid rgba(138,138,184,0.15)' }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(138,138,184,0.5)' }}>Amanah Domain</p>
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6 }}>{activeLens.amanahDomain}</p>
          </div>
        </div>

        {/* Daily Quest — Featured */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1" style={{ background: `${activeLens.color}20` }} />
            <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: `${activeLens.color}60` }}>Today's Quest</p>
            <div className="h-px flex-1" style={{ background: `${activeLens.color}20` }} />
          </div>
          <div className="rounded-2xl p-1" style={{ background: `linear-gradient(135deg, ${activeLens.color}15, transparent)`, border: `1px solid ${activeLens.color}35` }}>
            <div className="rounded-xl p-5" style={{ background: 'rgba(6,13,18,0.9)' }}>
              <div className="flex items-center gap-2 mb-4">
                <span className="font-mono text-[8px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: `${activeLens.color}15`, color: activeLens.color, border: `1px solid ${activeLens.color}30` }}>Daily</span>
                <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.25)' }}>{dailyDate}</span>
              </div>
              <QuestCard
                quest={dailyQuest}
                color={activeLens.color}
                activeLensId={activeLensId}
                amanahDomain={activeLens.amanahDomain}
                onComplete={handleComplete}
              />
            </div>
          </div>
        </div>

        {/* All Quests for This Lens */}
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.3)' }}>
            All {activeLens.title} Quests ({completedQuests.filter(id => activeLens.quests.some(q => q.id === id)).length}/{activeLens.quests.length} completed)
          </p>
          <div className="space-y-4">
            {activeLens.quests.map(quest => (
              <QuestCard
                key={quest.id}
                quest={quest}
                color={activeLens.color}
                activeLensId={activeLensId}
                amanahDomain={activeLens.amanahDomain}
                onComplete={handleComplete}
              />
            ))}
          </div>
        </div>

        {/* Completed Observations Summary */}
        {completedObservations.length > 0 && (
          <div className="mt-8 rounded-xl p-5" style={{ background: 'rgba(45,122,71,0.05)', border: '1px solid rgba(45,122,71,0.2)' }}>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(45,122,71,0.6)' }}>
              ✓ Quest Observations This Session — {completedObservations.length} recorded
            </p>
            <div className="space-y-2">
              {completedObservations.map((obs, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg p-3" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.12)' }}>
                  <span style={{ color: 'rgba(45,122,71,0.7)', fontSize: 12 }}>◎</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(45,122,71,0.7)' }}>{obs.questTitle}</p>
                    <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 10 }}>
                      Yaqeen: {obs.yaqeenLevel} · Names: {obs.namesExtracted.slice(0, 3).join(', ')}{obs.namesExtracted.length > 3 ? ` +${obs.namesExtracted.length - 3}` : ''}
                    </p>
                  </div>
                  <span className="font-mono text-[8px] px-2 py-0.5 rounded" style={{ background: 'rgba(45,122,71,0.1)', color: 'rgba(45,122,71,0.7)', border: '1px solid rgba(45,122,71,0.2)' }}>
                    {obs.yaqeenLevel}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 11, lineHeight: 1.6, marginTop: 12, fontStyle: 'italic' }}>
              "This recognition was decreed. You recognized what the Pen wrote. Your observation adds to the communal tafsir of the Qadr."
            </p>
          </div>
        )}

        {/* Fisabilillah Closing */}
        <div className="mt-10 rounded-xl p-6" style={{ background: 'rgba(45,122,71,0.05)', border: '1px solid rgba(45,122,71,0.2)', borderLeft: '4px solid rgba(45,122,71,0.4)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(45,122,71,0.6)' }}>Fisabilillah — In the Way of Allah</p>
          <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 14, lineHeight: 1.85, fontStyle: 'italic' }}>
            "Fisabilillah, in the way of Allah, you are entrusted with something that is running out, and you're entrusted with the means to do something in that period. What defines the time you're entrusted with, and what would it look like if you were mindful of Allah in those efforts?"
          </p>
          <p className="font-mono text-[9px] mt-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Open Letter — Al-Qadir Section</p>
        </div>
      </div>
    </section>
  );
}
